
import "./performance.css";

import Header from "../../components/Header";
import Footer from "../../components/Footer";

/* react */
import { useState, useEffect, useTransition } from "react";

/* banco de dados */
import { db, auth } from "../../services/firebaseConnection";
import { collection, getDocs, getDoc, doc, where, query, limit } from "firebase/firestore";

/* gráficos */
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList, ReferenceArea } from "recharts";

/* funções auxiliares */
import { wait, db_basic } from "../../services/functions/functions.ts";

/* links */
import { useNavigate } from "react-router-dom";


export default function Performance() {

    const [basic, setBasic] = useState([]);

    const [isPending1, startTransition1] = useTransition();

    const [loaded1, setLoaded1] = useState(false);

    const [dash1, setDash1] = useState(null);
    const [credits, setCredits] = useState(0);
    const [resumeCredits, setResumeCredits] = useState([]);
    const [chartCreditosMes, setChartCreditosMes] = useState([]);
    const [chartCreditosDia, setChartCreditosDia] = useState([]);
    const [creditosPorDiaState, setCreditosPorDiaState] = useState({});

    const [selectedMonth, setSelectedMonth] = useState(getMesAtual());

    const navigate = useNavigate();

    useEffect(() => { window.scrollTo(0, 0); }, []);

    useEffect(() => {

        const loadBasic = async () => {
            
            const uid = auth.currentUser.uid;
            const bbd = await db_basic(uid);
            setBasic(bbd);

        };

        loadBasic();

    }, []);


    useEffect(() => {

        if(!basic.agent) return;

        startTransition1(async () => {
            await dashs1();
            setLoaded1(true);
        });

    }, [basic.agent]);



    async function dashs1() {

        try {

            const tel = basic.agent.telefone;
            const dta = basic.agent.data_criacao.toDate();
            const start_date = dta.toISOString().split("T")[0];

            //busca o id do agente do gptmaker
            const telephonesdb = collection(db, "telephones");
            const telephonequery = query(telephonesdb, where("telefone", "==", tel), limit(1));
            const telephonedoc = await getDocs(telephonequery);
            
            if (telephonedoc.empty) {
                throw new Error("Telefone não encontrado");
            }
        
            const idg = telephonedoc.docs[0].data().id_gptmaker;

            wait(500);

            const resGptMaker = await fetch("https://gptmaker-services-712108985873.southamerica-east1.run.app/dashs1",
                { method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    agent_id: idg,
                    start_date: start_date,
                    })
                });
           
            const resultGptMaker = await resGptMaker.json();
            if(resultGptMaker.error) {
                return;
            }

            const resumo = resultGptMaker.resumo;

            //garante os últimos 12 meses
            const ultimos12 = gerarUltimos12Meses();

            //monta array no formato esperado pelo gráfico
            const chartData = ultimos12.map((key) => {
            const valores = resumo.por_mes?.[key] || { total: 0, resolvidos: 0, nao_resolvidos: 0 };
            return {
                mes: formatarMes(key),
                concluido: valores.resolvidos,
                nconcluido: valores.nao_resolvidos,
                };
            });

            const hoje = new Date();
            const anoAtual = hoje.getFullYear();
            const mesAtual = hoje.getMonth() + 1;

            const diasDoMes = gerarDiasDoMes(anoAtual, mesAtual);

            const chartDataDiario = diasDoMes.map((key) => {
            const valores = resumo.por_dia?.[key] || { total: 0, resolvidos: 0, nao_resolvidos: 0 };
            return {
                dia: key.split("-")[2], // só o dia para exibir no eixo x
                concluido: valores.resolvidos,
                nconcluido: valores.nao_resolvidos,
                };
            });

            setDash1({resumo, chart: chartData, chartDiario: chartDataDiario});

            //consulta o consumo de créditos do agente
            const diaAtual = hoje.getDate();

            const dataInicio = basic.agent.data_inicio_ativacao.toDate();

            const anoInicio = dataInicio.getFullYear();
            const mesInicio = dataInicio.getMonth() + 1;
            const diaInicio = dataInicio.getDate();

            let todosRegistros = [];

            for(let ano = anoInicio; ano <= anoAtual; ano++) {

                const res = await fetch("https://gptmaker-services-712108985873.southamerica-east1.run.app/agent/credits", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        agent_id: idg,
                        year: ano.toString()
                    })
                });

                const result = await res.json();

                if (!result.error && result.data) {
                    todosRegistros = todosRegistros.concat(result.data);
                }
            }

            dataInicio.setHours(0,0,0,0);
            hoje.setHours(23,59,59,999);

            let registrosFiltrados = [];

            todosRegistros.forEach(item => {

                const dataItem = new Date(item.year, item.month - 1, item.day);

                //normaliza também (boa prática)
                dataItem.setHours(12,0,0,0);

                if(dataItem >= dataInicio && dataItem <= hoje) {
                    registrosFiltrados.push(item);
                }
            });

            //cálculo do mês anterior (inclui virada de ano)
            const mesAnterior = mesAtual === 1 ? 12 : mesAtual - 1;
            const anoMesAnterior = mesAtual === 1 ? anoAtual - 1 : anoAtual;

            let totalCreditos = 0;

            const creditosPorMes = {};
            const creditosPorDia = {};

            registrosFiltrados.forEach(item => {

                const dataItem = new Date(item.year, item.month - 1, item.day);
                dataItem.setHours(12,0,0,0); //evita bug de timezone

                //filtro principal
                if(dataItem < dataInicio || dataItem > hoje) return;

                //total geral
                totalCreditos += item.credits;

                //chave mês
                const keyMes = `${item.year}-${String(item.month).padStart(2, "0")}`;

                if(!creditosPorMes[keyMes]) {
                    creditosPorMes[keyMes] = 0;
                }
                creditosPorMes[keyMes] += item.credits;

                //chave dia
                const keyDia = `${item.year}-${String(item.month).padStart(2, "0")}-${String(item.day).padStart(2, "0")}`;

                if(!creditosPorDia[keyDia]) {
                    creditosPorDia[keyDia] = 0;
                }
                creditosPorDia[keyDia] += item.credits;

            });

            const diffTime = hoje - dataInicio;
            const diffDias = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // media de créditos por dia
            const media = diffDias > 0 ? (totalCreditos / diffDias) : 0;

            // média de créditos por atendimento
            const totalAtendimentos = resumo.total_atendimentos || 0;

            const mediaPorAtendimento = totalAtendimentos > 0 ? (totalCreditos / totalAtendimentos) : 0;

            const chartMes = ultimos12.map((key) => ({mes: formatarMes(key), creditos: creditosPorMes[key] || 0}));

            setChartCreditosMes(chartMes);

            const diasDoMesAtual = gerarDiasDoMes(anoAtual, mesAtual);
            const chartDia = diasDoMesAtual.map((key) => ({dia: key.split("-")[2], creditos: creditosPorDia[key] || 0}));

            setChartCreditosDia(chartDia);

            setCreditosPorDiaState(creditosPorDia);

            setResumeCredits({total: totalCreditos, media: media.toFixed(1), mediaPorAtendimento: mediaPorAtendimento.toFixed(1)});

        }
        catch(erro) {
            console.log(erro);
        }
        
    }


    function formatarMes(yyyyMm) {

        const [ano, mes] = yyyyMm.split("-");
        const nomes = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
        return `${nomes[parseInt(mes, 10) - 1]}/${ano.slice(2)}`;

    }


    function gerarUltimos12Meses() {

        const meses = [];
        const agora = new Date();

        for (let i = 12; i >= 0; i--) {
            const d = new Date(agora.getFullYear(), agora.getMonth() - i, 1);
            const ano = d.getFullYear();
            const mes = String(d.getMonth() + 1).padStart(2, "0");
            meses.push(`${ano}-${mes}`);
        }

        return meses;
    }


    function gerarDiasDoMes(ano, mes) {

        //mês no JS é 0-indexado (0 = Janeiro), mas aqui vamos passar 1-indexado (1 = Janeiro)
        const dias = [];
        const ultimoDia = new Date(ano, mes, 0).getDate(); // último dia do mês

        for (let i = 1; i <= ultimoDia; i++) {
            const diaStr = String(i).padStart(2, "0");
            const mesStr = String(mes).padStart(2, "0");
            dias.push(`${ano}-${mesStr}-${diaStr}`);
        }

        return dias;
    }


    useEffect(() => {

        if (!selectedMonth || !dash1?.resumo) return;

        const [ano, mes] = selectedMonth.split("-");
        const diasDoMes = gerarDiasDoMes(Number(ano), Number(mes));

        const chartDataDiario = diasDoMes.map((key) => {
            const valores = dash1.resumo.por_dia?.[key] || { total: 0, resolvidos: 0, nao_resolvidos: 0 };
            return {dia: key.split("-")[2], concluido: valores.resolvidos, nconcluido: valores.nao_resolvidos,};
        });

        setDash1((prev) => ({ ...prev, chartDiario: chartDataDiario }));

        const chartCreditos = diasDoMes.map((key) => ({
            dia: key.split("-")[2],
            creditos: creditosPorDiaState?.[key] || 0
        }));

        setChartCreditosDia(chartCreditos);

    }, [selectedMonth, dash1?.resumo, creditosPorDiaState]);


    function getMesAtual() {

        const hoje = new Date();
        const ano = hoje.getFullYear();
        const mes = String(hoje.getMonth() + 1).padStart(2, "0");
        return `${ano}-${mes}`;

    }



    return(
        <div className="context">
        
            <Header access={1}></Header>

            <div className="content">

                <div className="ft_topico">Performance do agente</div>
                <div className="ft_descricao">Veja o desempenho do seu agente, compare períodos e métricas e obtenha mais informações sobre seu público e sua rotina.</div>
                <div className="space"></div>

                <div className="performance_resume">
                    
                    <div className="performance_resume_box1">
                        <div className="performance_resume_1a">
                            <div className="peformance_resume_box">
                                { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                                <>
                                <span className="peformance_resume_title" data-short="Atendimentos">Atendimentos</span>
                                <span className="peformance_resume_number" style={{color: "green"}}>{dash1.resumo?.total_atendimentos ?? 0}</span>
                                </>
                                )}
                            </div>
                        </div>
                        <div className="performance_resume_1b"><span className="performance_resume_signal">=</span></div>
                        <div className="performance_resume_1a">
                            <div className="peformance_resume_box">
                                { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                                <>
                                <span className="peformance_resume_title" data-short="Resolvidos">Resolvidos</span>
                                <span className="peformance_resume_number" style={{color: "blue"}}>{dash1.resumo?.resolvidos ?? 0}</span>
                                </>
                                )}
                            </div>
                        </div>
                        <div className="performance_resume_1b"><span className="performance_resume_signal">+</span></div>
                        <div className="performance_resume_1a">
                            <div className="peformance_resume_box">
                                { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                                <>
                                <span className="peformance_resume_title" data-short="Não resolvidos">Não resolvidos</span>
                                <span className="peformance_resume_number" style={{color: "red"}}>{dash1.resumo?.nao_resolvidos ?? 0}</span>
                                </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="performance_resume_box2">
                        <div className="performance_resume_1d"></div>
                    </div>

                    <div className="performance_resume_box3">
                        <div className="performance_resume_1c">
                            <div className="peformance_resume_box">
                                { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                                <>
                                <span className="peformance_resume_title" data-short="Tempo médio atendimento">Tempo médio de atendimento</span>
                                <span className="peformance_resume_number" style={{color: "green"}}>{dash1.resumo?.media_tempo_resolucao_formatado?.replace('min', ' minutos') ?? 0}</span>
                                </>
                                )}
                            </div>
                        </div>
                    </div>

                </div>

                <div className="space"></div>
            
                <div className="peformance_box">
                    <span className="peformance_box_space"></span>
                    <span className="peformance_resume_title" data-short="Número de atendimentos consolidados por mês">Número de atendimentos consolidados por mês</span>
                    <span className="peformance_box_space"></span>
                    <div className="performance_box_graphic">
                        { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                        <>
                        <ResponsiveContainer>
                            <BarChart margin={{ top: 20 }} data={dash1?.chart || []} onClick={(data) => {
                                if (data && data.activeLabel) {
                                    //Exemplo: "Out/25" → precisamos converter para "2025-10"
                                    const label = data.activeLabel;
                                    const partes = label.split("/");
                                    const mes = partes[0];
                                    const ano = "20" + partes[1];
      
                                    //Mapeia abreviação → número do mês
                                    const mapa = {
                                        Jan: "01", Fev: "02", Mar: "03", Abr: "04",
                                        Mai: "05", Jun: "06", Jul: "07", Ago: "08",
                                        Set: "09", Out: "10", Nov: "11", Dez: "12"
                                    };
                                    const mesNum = mapa[mes] || "01";

                                    setSelectedMonth(`${ano}-${mesNum}`);}
                                }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                            <YAxis hide={true} />
                            
                            {/* tooltip com o cursor cinza padrão */}
                            <Tooltip cursor={{ fill: "rgba(200, 200, 200, 0.2)" }} formatter={(value, name) => {
                                    if (name === "concluido") return [value, "Resolvido"];
                                    if (name === "nconcluido") return [value, "Não Resolvido"];
                            return [value, name];}}/>


                            {/* destaque fixo no mês selecionado */}
                            {selectedMonth && (
                            <ReferenceArea
                                x1={dash1?.chart?.find((item) => item.mes === formatarMes(selectedMonth))?.mes}
                                x2={dash1?.chart?.find((item) => item.mes === formatarMes(selectedMonth))?.mes}
                                ifOverflow="extendDomain"
                                fill="rgba(255, 215, 0, 0.8)" // amarelo translúcido
                            />
                            )}

                            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value, entry) => {if (value === 'concluido') return 'Resolvido'; if (value === 'nconcluido') return 'Não Resolvido'; return value;}} />
                            <Bar dataKey="concluido" stackId="a" fill="blue" shape={(props) => (<rect {...props} tabIndex={-1} style={{ cursor: "pointer" }} />)} />
                            <Bar dataKey="nconcluido" stackId="a" fill="red" shape={(props) => (<rect {...props} tabIndex={-1} style={{ cursor: "pointer" }} />)} >
                            <LabelList
                                dataKey="nconcluido"
                                position="top"
                                content={(props) => {
                                    const { x, y, width, index } = props;
                                    const total = (dash1?.chart?.[index]?.concluido || 0) + (dash1?.chart?.[index]?.nconcluido || 0);
                                    return (<text x={x + width / 2} y={y - 5} textAnchor="middle" fill="black" fontSize={12}>{total}</text>);
                                }} />
                            </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        </>
                        )}
                    </div>
                    <span className="peformance_box_space"></span>
                </div>
                <div className="space"></div>

                <div className="peformance_box">
                    <span className="peformance_box_space"></span>
                    <span className="peformance_resume_title" data-short={`Número de atendimentos consolidados por dia\n(Período: ${formatarMes(selectedMonth)})`}>Número de atendimentos consolidados por dia {"(Período: " + formatarMes(selectedMonth) + ")"}</span>
                    <span className="peformance_box_space"></span>
                    <div className="performance_box_graphic">
                        { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                        <>
                        <ResponsiveContainer>
                            <BarChart margin={{ top: 20 }} data={dash1?.chartDiario || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="dia" tick={{ fontSize: 10 }} />
                            <YAxis hide={true} />
                            <Tooltip formatter={(value, name) => {
                                if (name === "concluido") return [value, "Resolvido"];
                                if (name === "nconcluido") return [value, "Não Resolvido"];
                            return [value, name];}}/>

                            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value, entry) => {if (value === 'concluido') return 'Resolvido'; if (value === 'nconcluido') return 'Não Resolvido'; return value;}} />
                            <Bar dataKey="concluido" stackId="a" fill="blue" />
                            <Bar dataKey="nconcluido" stackId="a" fill="red">
                            <LabelList
                                dataKey="nconcluido"
                                position="top"
                                content={(props) => {
                                    const { x, y, width, index } = props;
                                    const total = (dash1?.chartDiario?.[index]?.concluido || 0) + (dash1?.chartDiario?.[index]?.nconcluido || 0);
                                    return (<text x={x + width / 2} y={y - 5} textAnchor="middle" fill="black" fontSize={12}>{total}</text>);
                                }} />
                            </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        </>
                        )}
                    </div>
                    <span className="peformance_box_space"></span>
                </div>

                <div className="space"></div>

                <div className="performance_resume">
                    
                    <div className="performance_resume_box4">
                        <div className="performance_resume_1e">
                            <div className="peformance_resume_box">
                                { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                                <>
                                <span className="peformance_resume_title" data-short="Atendimentos">Uso total de créditos</span>
                                <span className="peformance_resume_number" style={{color: "green"}}>{resumeCredits.total ?? 0}</span>
                                </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="performance_resume_box5">
                        <div className="performance_resume_1d"></div>
                    </div>

                    <div className="performance_resume_box6">
                        <div className="performance_resume_1f">
                            <div className="peformance_resume_box">
                                { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                                <>
                                <span className="peformance_resume_title" data-short="Tempo médio atendimento">Média uso de créditos por atendimento</span>
                                <span className="peformance_resume_number" style={{color: "green"}}>{resumeCredits.mediaPorAtendimento ?? 0}</span>
                                </>
                                )}
                            </div>
                        </div>
                        <div className="performance_resume_1g"></div>
                        <div className="performance_resume_1f">
                            <div className="peformance_resume_box">
                                { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                                <>
                                <span className="peformance_resume_title" data-short="Tempo médio atendimento">Média uso de créditos por dia</span>
                                <span className="peformance_resume_number" style={{color: "green"}}>{resumeCredits.media ?? 0}</span>
                                </>
                                )}
                            </div>
                        </div>
                    </div>

                </div>

                <div className="space"></div>
            
                <div className="peformance_box">
                    <span className="peformance_box_space"></span>
                    <span className="peformance_resume_title" data-short="Número de créditos usados por mês">Número de créditos usados por mês</span>
                    <span className="peformance_box_space"></span>
                    <div className="performance_box_graphic">
                        { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                        <>
                        <ResponsiveContainer>
                            <BarChart margin={{ top: 20 }} data={chartCreditosMes  || []} onClick={(data) => {
                                if (data && data.activeLabel) {
                                    //Exemplo: "Out/25" → precisamos converter para "2025-10"
                                    const label = data.activeLabel;
                                    const partes = label.split("/");
                                    const mes = partes[0];
                                    const ano = "20" + partes[1];
      
                                    //Mapeia abreviação → número do mês
                                    const mapa = {
                                        Jan: "01", Fev: "02", Mar: "03", Abr: "04",
                                        Mai: "05", Jun: "06", Jul: "07", Ago: "08",
                                        Set: "09", Out: "10", Nov: "11", Dez: "12"
                                    };
                                    const mesNum = mapa[mes] || "01";

                                    setSelectedMonth(`${ano}-${mesNum}`);}
                                }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                            <YAxis hide={true} />
                            
                            {/* tooltip com o cursor cinza padrão */}
                            <Tooltip cursor={{ fill: "rgba(200, 200, 200, 0.2)" }} formatter={(value) => [value, "Créditos"]} />
                            
                            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => "Créditos"} />

                            {/* destaque fixo no mês selecionado */}
                            {selectedMonth && (
                            <ReferenceArea
                                x1={dash1?.chart?.find((item) => item.mes === formatarMes(selectedMonth))?.mes}
                                x2={dash1?.chart?.find((item) => item.mes === formatarMes(selectedMonth))?.mes}
                                ifOverflow="extendDomain"
                                fill="rgba(255, 215, 0, 0.8)" // amarelo translúcido
                            />
                            )}

                            <Bar dataKey="creditos" fill="blue" shape={(props) => (<rect {...props} style={{ cursor: "pointer" }} />)} >
                                <LabelList dataKey="creditos" position="top" content={(props) => {
                                const { x, y, width, value } = props;
                                return (<text x={x + width / 2} y={y - 5} textAnchor="middle" fill="black" fontSize={12}>{value}</text>);}}/>
                            </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        </>
                        )}
                    </div>
                    <span className="peformance_box_space"></span>
                </div>
                <div className="space"></div>

                <div className="peformance_box">
                    <span className="peformance_box_space"></span>
                    <span className="peformance_resume_title" data-short={`Número de créditos usados por dia\n(Período: ${formatarMes(selectedMonth)})`}>Número de créditos usados por dia {"(Período: " + formatarMes(selectedMonth) + ")"}</span>
                    <span className="peformance_box_space"></span>
                    <div className="performance_box_graphic">
                        { !loaded1 ? (<><span className="performance_load">carregando ...</span></>) : (
                        <>
                        <ResponsiveContainer>
                            <BarChart margin={{ top: 20 }} data={chartCreditosDia || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="dia" tick={{ fontSize: 10 }} />
                            <YAxis hide={true} />
                            <Tooltip formatter={(value) => [value, "Créditos"]} />
                            <Legend wrapperStyle={{ fontSize: 11 }} formatter={() => "Créditos"} />
                            <Bar dataKey="creditos" fill="blue">
                            <LabelList dataKey="creditos" position="top" content={(props) => {
                            const { x, y, width, value } = props;
                            if(!value) return null;
                            return (
                            <text x={x + width / 2} y={y - 5} textAnchor="middle" fill="black" fontSize={12}>{value}</text>
                            );}} />
                            </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        </>
                        )}
                    </div>
                    <span className="peformance_box_space"></span>
                </div>

                <div className="space"></div>
                <div className="space"></div>
                <div className="button_back_form"><button type="button" name="btbck" className="button2_form" onClick={() => navigate("/panel")}>VOLTAR</button></div>
                <div className="space"></div>
                <div className="space"></div>
                    
            </div>
                    
        <Footer access={1}></Footer>
                    
        </div>
    )
}